import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { inject, type InjectionKey, type Plugin } from "vue";
import { useUserSession } from "@/stores/userSession";
import type { ToastServiceMethods } from "primevue/toastservice";
import { type Router } from "vue-router";

type axiosParam = {
  toast: ToastServiceMethods;
  router: Router;
};

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  errorMessage?: Function | string;
}

interface AxiosCustomInstance {
  httpClient: AxiosInstance;
  $request: (config: CustomAxiosRequestConfig) => Promise<any>;
  $get: (url: string, config?: CustomAxiosRequestConfig) => Promise<any>;
  $delete: (url: string, config?: CustomAxiosRequestConfig) => Promise<any>;
  $post: (url: string, data?: any, config?: CustomAxiosRequestConfig) => Promise<any>;
  post: (url: string, data?: any, config?: CustomAxiosRequestConfig) => Promise<any>;
  $put: (url: string, data?: any, config?: CustomAxiosRequestConfig) => Promise<any>;
  $patch: (
    url: string,
    data?: any,
    config?: CustomAxiosRequestConfig
  ) => Promise<any>;
}
const symbol = Symbol("useAxios") as InjectionKey<AxiosCustomInstance>;

function Axios({ toast, router }: axiosParam): AxiosCustomInstance {
  const httpClient = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    responseType: "json",
  });

  httpClient.interceptors.request.use((req: any) => {
    const userSession = useUserSession();
    // Only attach the Authorization header when there is a token; requests made
    // without a session (e.g. login) are sent without it.
    if (userSession.token) {
      req.headers = {
        ...req.headers,
        Authorization: `${userSession.token}`,
      };
    }
    return req;
  });

  httpClient.interceptors.response.use(
    (response) => {
      if (response.data.status === "failed") {
        toast.remove({ group: "message" });
        toast.add({
          group: "message",
          detail: response.data.message,
          severity: "error",
          life: 5000,
          closable: true,
        });
      }
      return response;
    },
    (error) => {
      let { errorMessage } = error.config;
      if (typeof errorMessage === 'function') {
        errorMessage = errorMessage(error);
      }
      if (errorMessage === true || (errorMessage === undefined && error.config.method !== 'get')) {
        // TODO Show the fallback message.
      }
      else if (errorMessage) {
        toast.add({
          group: 'message',
          detail: errorMessage,
          severity: 'error',
          closable: true,
        });
      }
      const userSession = useUserSession();
      if (error?.response?.data?.message === "INVALID_SESSION_ID") {
        const isPsEstimationRequest = `${error?.config?.url || ""}`.includes("/ps-estimation/");
        const emptyToken = {
          ccrm_access_token: "",
          access_token: "",
          active: false,
          exp: 0,
          iat: 0,
        };

        if (isPsEstimationRequest) {
          userSession.setPsEstimationAuthDetail(emptyToken);
          router.push({ name: "ps-estimate.list" });
        } else {
          userSession.setPsCustomerAuthDetail(emptyToken);
          router.push({ name: "ps-customer-server.list" });
        }
      }
      if (error?.response?.status === 401) {
        userSession.logoutUser();
        router.push('/login');
      }
      return Promise.reject(error);
    }
  );

  return {
    httpClient,
    $request: async (config: CustomAxiosRequestConfig) =>
      (await httpClient.request(config)),
    $get: async (url: string, config?: CustomAxiosRequestConfig) =>
      (await httpClient.get(url, config)).data,
    $delete: async (url: string, config?: CustomAxiosRequestConfig) =>
      (await httpClient.delete(url, config)).data,
    $post: async (url: string, data?: any, config?: CustomAxiosRequestConfig) =>
      (await httpClient.post(url, data, config)).data,
    $put: async (url: string, data?: any, config?: CustomAxiosRequestConfig) =>
      (await httpClient.put(url, data, config)).data,
    $patch: async (url: string, data?: any, config?: CustomAxiosRequestConfig) =>
      (await httpClient.patch(url, data, config)).data,
    post: async (url: string, data?: any, config?: CustomAxiosRequestConfig) =>
      (await httpClient.post(url, data, config)),
  };
}

export const axiosPlugin: Plugin = {
  install(app, options) {
    app.provide(
      symbol,
      Axios({
        toast: app.config.globalProperties.$toast,
        router: app.config.globalProperties.$router,
      })
    );
  },
};

export function useAxios(): AxiosCustomInstance {
  return inject(symbol) as AxiosCustomInstance;
}
